import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  UserPlus,
  BookOpen,
  MoreVertical,
  MessageSquare,
  Mail,
  DollarSign,
  Edit,
  Trash2,
  FileText,
  X,
} from 'lucide-react';

interface Teacher {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  subject: string;
  status: 'active' | 'inactive';
  coursesCount: number;
  studentsCount: number;
}

const mockTeachers: Teacher[] = [
  { id: '1', name: 'Dr. Sarah Cohen', email: 'sarah@school.com', subject: 'Mathematics', status: 'active', coursesCount: 3, studentsCount: 45 },
  { id: '2', name: 'Prof. Michael Levy', email: 'michael@school.com', subject: 'Physics', status: 'active', coursesCount: 2, studentsCount: 32 },
  { id: '3', name: 'Ms. Rachel Green', email: 'rachel@school.com', subject: 'English', status: 'active', coursesCount: 4, studentsCount: 56 },
  { id: '4', name: 'Mr. David Ben-Ari', email: 'david@school.com', subject: 'History', status: 'inactive', coursesCount: 1, studentsCount: 18 },
  { id: '5', name: 'Dr. Anna Shapiro', email: 'anna@school.com', subject: 'Chemistry', status: 'active', coursesCount: 2, studentsCount: 28 },
];

export function SchoolTeachersTab() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTeachers, setSelectedTeachers] = useState<string[]>([]);
  const [contextMenuId, setContextMenuId] = useState<string | null>(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedTeacherForAssign, setSelectedTeacherForAssign] = useState<Teacher | null>(null);

  const filteredTeachers = mockTeachers.filter(
    (teacher) =>
      teacher.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      teacher.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      teacher.subject.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const toggleSelectAll = () => {
    if (selectedTeachers.length === filteredTeachers.length) {
      setSelectedTeachers([]);
    } else {
      setSelectedTeachers(filteredTeachers.map((t) => t.id));
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedTeachers((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const openAssignModal = (teacher: Teacher) => {
    setSelectedTeacherForAssign(teacher);
    setShowAssignModal(true);
  };

  return (
    <div className="space-y-4">
      {/* Header Actions */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search teachers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 w-64 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Reports */}
          <button
            onClick={() => alert('Reports feature coming soon!')}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <FileText className="w-4 h-4" />
            Reports
          </button>

          {/* Add Teacher */}
          <button
            onClick={() => navigate('/admin/settings/invite-teachers')}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700"
          >
            <UserPlus className="w-4 h-4" />
            Add Teacher
          </button>
        </div>
      </div>

      {/* Teachers Count */}
      <div className="text-sm text-gray-500">
        {filteredTeachers.length} Teachers
      </div>

      {/* Teachers Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="w-12 px-4 py-3">
                <input
                  type="checkbox"
                  checked={selectedTeachers.length === filteredTeachers.length && filteredTeachers.length > 0}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 text-teal-600 rounded focus:ring-teal-500"
                />
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Teacher</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Courses</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Students</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="w-24 px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredTeachers.map((teacher) => (
              <tr key={teacher.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedTeachers.includes(teacher.id)}
                    onChange={() => toggleSelect(teacher.id)}
                    className="w-4 h-4 text-teal-600 rounded focus:ring-teal-500"
                  />
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-medium">
                      {teacher.name.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{teacher.name}</p>
                      <p className="text-sm text-gray-500">{teacher.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">{teacher.subject}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{teacher.coursesCount} courses</td>
                <td className="px-4 py-3 text-sm text-gray-600">{teacher.studentsCount} students</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    teacher.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {teacher.status === 'active' ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-center gap-1">
                    <button
                      onClick={() => openAssignModal(teacher)}
                      className="p-1.5 text-gray-500 hover:text-teal-600 hover:bg-teal-50 rounded"
                      title="Assign to Course"
                    >
                      <BookOpen className="w-4 h-4" />
                    </button>
                    <button
                      className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                      title="Send Message"
                    >
                      <MessageSquare className="w-4 h-4" />
                    </button>
                    <div className="relative">
                      <button
                        onClick={() => setContextMenuId(contextMenuId === teacher.id ? null : teacher.id)}
                        className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                      {contextMenuId === teacher.id && (
                        <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                          <button className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                            <Edit className="w-4 h-4" />
                            Edit Profile
                          </button>
                          <button className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                            <BookOpen className="w-4 h-4" />
                            Manage Courses
                          </button>
                          <button className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                            <Mail className="w-4 h-4" />
                            Send Mail
                          </button>
                          <button className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                            <MessageSquare className="w-4 h-4" />
                            Send Message
                          </button>
                          <button className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                            <DollarSign className="w-4 h-4" />
                            See Finance
                          </button>
                          <hr className="my-1" />
                          <button className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2">
                            <Trash2 className="w-4 h-4" />
                            Delete Teacher
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Assign to Course Modal */}
      {showAssignModal && selectedTeacherForAssign && (
        <AssignToCourseModal
          teacher={selectedTeacherForAssign}
          onClose={() => {
            setShowAssignModal(false);
            setSelectedTeacherForAssign(null);
          }}
        />
      )}
    </div>
  );
}

function AssignToCourseModal({ teacher, onClose }: { teacher: Teacher; onClose: () => void }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCourses, setSelectedCourses] = useState<string[]>(['1', '3']);

  const courses = [
    { id: '1', name: 'Introduction to Psychology', price: '$150/month', enrolled: true },
    { id: '2', name: 'Web Development Bootcamp', price: '$200/month', enrolled: false },
    { id: '3', name: 'Data Science Fundamentals', price: '$180/month', enrolled: true },
    { id: '4', name: 'Digital Marketing Strategies', price: '$120/month', enrolled: false },
  ];

  const toggleCourse = (id: string) => {
    setSelectedCourses((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Students & Teacher - Attach to course</h2>
          <button onClick={onClose} className="p-1 text-gray-500 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          <div className="grid grid-cols-2 gap-6">
            {/* Teacher Courses */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">Teacher Courses</h3>

              {/* Teacher Profile Preview */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg mb-4">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-medium">
                  {teacher.name.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                </div>
                <div>
                  <p className="font-medium text-gray-900 text-sm">{teacher.name}</p>
                </div>
              </div>

              {/* Search */}
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search courses..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>

              {/* Courses List */}
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {courses.map((course) => (
                  <label
                    key={course.id}
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedCourses.includes(course.id)}
                      onChange={() => toggleCourse(course.id)}
                      className="w-4 h-4 text-teal-600 rounded focus:ring-teal-500"
                    />
                    <div className="flex-1">
                      <p className="text-sm text-gray-900">{course.name}</p>
                      <p className="text-xs text-gray-500">{course.price}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Student Enrollment */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">Student Enrollment</h3>

              {/* Placeholder for student info */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg mb-4">
                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-medium">
                  SN
                </div>
                <div>
                  <p className="font-medium text-gray-900 text-sm">Student Name</p>
                </div>
              </div>

              {/* Courses List */}
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {courses.map((course) => (
                  <label
                    key={course.id}
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedCourses.includes(course.id)}
                      onChange={() => toggleCourse(course.id)}
                      className="w-4 h-4 text-teal-600 rounded focus:ring-teal-500"
                    />
                    <div className="flex-1">
                      <p className="text-sm text-gray-900">{course.name}</p>
                      <p className="text-xs text-gray-500">{course.price}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
